import { cloneDeep } from 'lodash';
import Backbone from 'backbone-model';
import URI from 'urijs';

import ChatSender from 'entities/ChatSender';
import Entity from 'lib/Entity';
// @ts-ignore
import Group from 'entities/Group';
// @ts-ignore
import Message from 'entities/Message';
// @ts-ignore
import MessageSender from 'entities/MessageSender';
// @ts-ignore
import ReactionSummary from 'entities/ReactionSummary';
// @ts-ignore
import User from 'entities/User';
import uuid from 'uuid/v4';
// @ts-ignore
import LinkPreview from 'entities/LinkPreview';
// @ts-ignore
import { PhoneCallStatus } from 'phoneCalls/reducers/phoneCalls';
import { Backbone$Id } from 'types/typescript/Backbone';
import { PhoneCallMessageGQL } from 'types/typescript/PhoneCallGQL';
import { MessageTranslationsInput } from 'globalTypes';
import { V2File } from 'clients/api/fileUploads';

export class ChatBaseMessage extends Entity {
  initialize() {
    this.idAttribute = 'uuid';
  }

  getSeq(): number | undefined {
    return this.get('seq');
  }

  setChatId(id: Backbone$Id) {
    const chat = this.get('chat');
    chat.uuid = id;
    this.set('chat', chat);
  }

  getChatId(): string {
    return this.get('chat').uuid;
  }

  getType(): string {
    return this.get('type');
  }
}

export class ChatMessage extends ChatBaseMessage {
  defaults() {
    return {
      body: '',
      preview: '',
      created_at: new Date(Date.now()),
      type: 'text',
      body_attributes: [],
      link_previews: [],
    };
  }

  urlRoot = (): string => {
    return `/chats/${this.getChatId()}/messages`;
  };

  parse(response: any) {
    const message = cloneDeep(response);
    if ('created_at' in message) {
      message['created_at'] = new Date(message['created_at']);
    }
    message['sender'] = ChatSender.fromJSON(message['sender']);

    if (message['message']) {
      message['message'] = Message.fromJSON(message['message']);
    }

    if (message['group_summary']) {
      message['group_summary'] = Group.fromJSON(message['group_summary']);
    }

    if (message['reaction_summary']) {
      message['reaction_summary'] = ReactionSummary.fromJSON(message['reaction_summary']);
    }

    if (message['link_previews']) {
      message['link_previews'] = message['link_previews'].map((linkPreview: any) =>
        LinkPreview.fromJSON(linkPreview)
      );
    }

    return message;
  }

  getLinkPreviews(): [LinkPreview] {
    return this.get('link_previews');
  }

  getReactionSummary(): ReactionSummary {
    return this.get('reaction_summary') || new ReactionSummary();
  }

  canReact(): boolean {
    return !!this.get('can_react');
  }

  getSender(): ChatSender | User | MessageSender {
    const sender = this.get('sender');
    // the reason we need to check both the signature and the name here
    // is because the GQL mutation that loads a scheduled message into the
    // Apollo cache returns both a `name` and a `signature` field,
    // while the v2 endpoint that loads scheduled messages into Flux only
    // returns a misnamed `name` field (which ACTUALLY returns a signature).
    const signatureField = sender.get('signature');
    if (signatureField) {
      sender.set('name', signatureField);
    }
    return sender;
  }

  getGroupSummary() {
    return this.get('group_summary');
  }

  getGroupIds(): Array<number> {
    return this.getGroupSummary() ? [this.getGroupSummary().id] : [];
  }

  getBody(): string | undefined {
    return this.get('body');
  }

  setPreview(body?: string): Backbone.Model {
    return this.set({ preview: body });
  }

  getPreview() {
    return this.get('preview') || this._generatePreview();
  }

  _generatePreview() {
    const preview = this.getBody() || (this.getFile() && '1 Attachment 📚') || '<error>';
    if (this.getType() === 'text') {
      return `You: ${preview}`;
    } else if (this.getType() === 'broadcast') {
      return `Announcement: ${preview}`;
    }
    return preview;
  }
  getISO639Locale() {
    let locale = this.get('locale');
    if (locale) {
      [locale] = locale.split('-');
    }
    return locale;
  }
  getFile() {
    return this.get('file');
  }
  getCreatedAt() {
    return this.get('created_at');
  }
  getSentAt() {
    return this.getCreatedAt();
  }
  getReadSummary() {
    return this.get('read_summary');
  }
  getUuid() {
    return this.get('uuid');
  }
  getOriginGroupId() {
    return this.get('origin_group_id');
  }
  getBodyAttributes(): BodyAttribute[] {
    return this.get('body_attributes');
  }
  toGraphQL() {
    const files = [];
    const file = this.getFile();

    if (file) {
      files.push({
        uuid: file.id,
        size: file.size,
        name: file.name,
        url: file.urls.cdn,
        contentType: file.content_type,
        previewWidth: file.preview_width || 0,
        previewHeight: file.preview_height || 0,
        __typename: 'File',
      });
    }
    let groupSummary = null;

    if (this.getGroupSummary()) {
      groupSummary = {
        __typename: 'Class',
        ...this.getGroupSummary().toJSON(),
      };
    }
    let sender;
    const senderField = this.getSender();

    if (senderField) {
      sender =
        // XXX if the sender type is district, construct an
        // EntitySender. otherwise, construct a UserSender.
        senderField.get('type') === 'district'
          ? {
              uuid: senderField.get('uuid'),
              name: senderField.get('name'),
              avatarUrl: 'https://remind.imgix.net/0b34f158-c3d0-4321-8cd7-aa6ec744e6d6/school.svg',
              __typename: 'EntitySender',
            }
          : {
              id: senderField.get('id'),
              uuid: senderField.get('uuid'),
              name: senderField.get('display_name') || senderField.get('name'),
              initials: senderField.get('initials'),
              avatarUrl: senderField.get('avatarUrl') || null,
              contactabilityStatus: senderField.get('contactabilityStatus') || null,
              reachabilityInfo: senderField.get('reachabilityInfo') || null,
              signature: senderField.get('signature') || null,
              __typename: 'UserSender',
            };
    }
    const readSummary = this.getReadSummary();
    const date = new Date();
    return {
      key: `${this.id}.${this.getSeq() || 0}`,
      seq: (this.getSeq() || 0).toString(),
      item: {
        type: this.getType(),
        uuid: this.id || this.get('stream_item_uuid'),
        canReact: this.canReact(),
        locale: this.getISO639Locale(),
        body: this.getBody(),
        createdAt: new Date(),
        reactionSummary: null,
        bodyAttributes: [],
        publicUrl: null,
        threads: [],
        linkPreviews: [],
        status: null,
        readSummary: {
          state: readSummary && readSummary.state ? readSummary.state : 'sending',
          __typename: 'MessageReadSummary',
        },
        preview: this.getPreview(),
        sentAt: date.toISOString(),
        updatedAt: date.toISOString(),
        sendAt: null,
        chat: {
          uuid: this.get('stream_uuid') || this.getChatId(),
          __typename: 'ChatStream',
        },
        sender,
        target: null,
        files,
        group: groupSummary,
        urgent: this.get('urgent') || false,
        translationDetails: this.get('translation_details') || null,
        __typename: 'MessageItem',
      },
      __typename: 'SequenceItem',
    };
  }
  send(translations?: MessageTranslationsInput) {
    return this.save(
      {},
      {
        url: this.urlRoot(),
        // @ts-ignore - backbone doesnt know method...
        method: 'create',
        data: {
          message: {
            body: this.getBody(),
            uuid: this.id,
            file: this.getFile(),
            origin_group_id: this.getOriginGroupId(),
            translations: translations && {
              original_language_code: translations.originalLanguageCode,
              target_language_codes: translations.targetLanguageCodes,
              predefined_translations:
                translations.predefinedTranslations &&
                translations.predefinedTranslations
            },
          },
        },
      }
    );
  }

  static create(
    text: any,
    file: V2File | undefined,
    chat: any,
    sender: any,
    seq: any,
    type: any = 'text',
    groupSummary: any,
    originGroupId: any,
    locale: any
  ) {
    return new ChatMessage({
      uuid: uuid(),
      sender,
      chat: {
        uuid: chat.id,
      },
      body: text,
      failed: false,
      seq,
      file,
      type,
      group_summary: groupSummary,
      origin_group_id: originGroupId,
      locale: locale || (sender.getISO639Locale ? sender.getISO639Locale() : 'en'),
    });
  }
}
export class ChatSystemMessage extends ChatMessage {
  getFile(): V2File | undefined {
    return this.get('file');
  }
}
export class ChatGapMessage extends ChatBaseMessage {
  getParams() {
    return this.get('params');
  }
  getUuid() {
    return 'gap-uuid';
  }
}
export type PhoneCallMessageData = {
  uuid: string;
  caller_uuid?: string;
  callee_uuid?: string;
  status: PhoneCallStatus;
  duration: number;
  note: string;
  reasons: string[];
};

type PhoneCallMessageCTA = {
  text: string;
  link: string;
};

export class PhoneCallMessage extends ChatBaseMessage {
  parse(response: any) {
    const message = cloneDeep(response);
    if ('created_at' in message) {
      message['created_at'] = new Date(message['created_at']);
    }
    message['sender'] = ChatSender.fromJSON(message['sender']);
    return message;
  }
  getBody(): string | undefined {
    return this.get('body');
  }
  getCall(): PhoneCallMessageData {
    return this.get('phone_call');
  }
  getSentAt() {
    return this.get('created_at');
  }
  getSender(): ChatSender {
    return this.get('sender');
  }
  getUuid() {
    return this.get('uuid');
  }
  getFormattedDuration(): string | undefined {
    const { duration } = this.getCall();
    if (!duration) {
      return undefined;
    }
    const sec = duration % 60;
    const min = Math.floor(duration / 60);
    if (min && sec) {
      return `${min}m ${sec}s`;
    } else if (min) {
      return `${min}m`;
    } else if (sec) {
      return `${sec}s`;
    } else {
      return undefined;
    }
  }
  getCTA(): PhoneCallMessageCTA | undefined {
    return this.get('call_to_action');
  }
  getParsedCTALink(): any | undefined {
    const cta = this.getCTA();
    if (!cta) {
      return undefined;
    }
    const uri = URI(cta.link); // rmd://call?callee_uuid=2aa65b0b-d13d-4eed-ba52-a49dfa23e80d
    if (uri.host() === 'call') {
      return {
        action: 'call',
        calleeUuid: (uri.query(true) as any).callee_uuid,
      };
    }
    return undefined;
  }
  toGraphQL(): PhoneCallMessageGQL {
    return {
      uuid: this.get('uuid'),
      body: this.get('body'),
      createdAt: this.get('created_at').toISOString(),
      phoneCall: this.get('phone_call'),
      callToAction: this.get('call_to_action'),
      sender: {
        id: this.getSender().id,
        name: this.getSender().getName(),
        uuid: this.getSender().getUuid(),
        initials: this.getSender().getInitials() || '??',
        avatarUrl: this.getSender().get('avatarUrl'),
        contactabilityStatus: this.getSender().get('contactabilityStatus'),
      },
      chat: {
        uuid: this.getChatId(),
      },
    };
  }
}
export function chatMessageFactory(data: any) {
  if (data.type === 'system') {
    return ChatSystemMessage.fromJSON(data);
  } else if (data.type === 'phone_call') {
    return PhoneCallMessage.fromJSON(data);
  } else {
    return ChatMessage.fromJSON(data);
  }
}
export function shouldListenToPushEventFrom(message: ChatMessage | PhoneCallMessage): boolean {
  return ['text', 'broadcast', 'system'].includes(message.getType()) && !!message.get('chat');
}
export const chatMessageTypes = {
  gap: ChatGapMessage,
  system: ChatSystemMessage,
  attachment: ChatMessage,
  text: ChatMessage,
  broadcast: ChatMessage,
  phone_call: PhoneCallMessage,
};

export default ChatMessage;
